import React from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Alert,
  Pagination,
} from '@mui/material';
import { Person as PersonIcon, Place as PlaceIcon } from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import apiService from '../services/api';
import { AnnouncementCreatedAtCaption } from '../components/AnnouncementCreatedAtCaption';

const LinkedAnnouncementsPage: React.FC = () => {
  const [page, setPage] = React.useState(1);

  const { data, isLoading, error } = useQuery({
    queryKey: ['linked-user-announcements', page],
    queryFn: () => apiService.getLinkedUserAnnouncements({ page, limit: 20 }),
  });

  const announcements = data?.announcements ?? [];
  const totalPages = data?.pagination?.pages ?? 1;

  return (
    <Box sx={{ maxWidth: '100%' }}>
      <Typography variant="h4" component="h1" gutterBottom sx={{ fontSize: { xs: '1.2rem', sm: '2rem' } }}>
        Annonces liées à votre établissement
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Objets perdus ou trouvés déclarés par des utilisateurs en lien avec votre
        établissement (annonces non publiées sur le fil général Sherlook).
      </Typography>

      {isLoading && (
        <Box display="flex" justifyContent="center" p={4}>
          <CircularProgress />
        </Box>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Impossible de charger les annonces. Vérifiez votre connexion ou les droits du compte.
        </Alert>
      )}

      {!isLoading && !error && announcements.length === 0 && (
        <Alert severity="info">Aucune annonce liée pour le moment.</Alert>
      )}

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {announcements.map((a) => (
          <Card key={a.id} variant="outlined">
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="flex-start" gap={2} flexWrap="wrap">
                <Box>
                  <Typography variant="h6" component="h2">
                    {a.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    {a.description?.length > 200 ? `${a.description.slice(0, 200)}…` : a.description}
                  </Typography>
                  <Box display="flex" alignItems="center" gap={1} mt={1} flexWrap="wrap">
                    <Chip
                      size="small"
                      label={a.type === 'lost' ? 'Perdu' : 'Trouvé'}
                      color={a.type === 'lost' ? 'warning' : 'success'}
                    />
                    <Chip size="small" label={a.category?.name} variant="outlined" />
                    <AnnouncementCreatedAtCaption createdAt={a.created_at} />
                  </Box>
                  <Box display="flex" alignItems="center" gap={0.5} mt={1}>
                    <PersonIcon fontSize="small" color="action" />
                    <Typography variant="body2">
                      {a.user?.first_name} {a.user?.last_name}
                    </Typography>
                    {a.location?.city && (
                      <>
                        <PlaceIcon fontSize="small" color="action" sx={{ ml: 1 }} />
                        <Typography variant="body2">{a.location.city}</Typography>
                      </>
                    )}
                  </Box>
                </Box>
              </Box>
            </CardContent>
          </Card>
        ))}
      </Box>

      {totalPages > 1 && (
        <Box display="flex" justifyContent="center" mt={3} sx={{ px: 1, overflowX: 'auto' }}>
          <Pagination
            count={totalPages}
            page={page}
            onChange={(_, p) => setPage(p)}
            color="primary"
            size="small"
            siblingCount={0}
            boundaryCount={1}
            sx={{ flexShrink: 0 }}
          />
        </Box>
      )}
    </Box>
  );
};

export default LinkedAnnouncementsPage;
